import React, { useState } from 'react';
import './App.scss';

const projects = [
  {
    id: 0,
    title: 'Discovery TV App',
    video: '/videos/discovery.mp4',
    tags: [
      'Complex Navigation Patterns',
      'Large Format Interface Design',
      'React + Firebase (Production)',
      'Cross-Platform Workflows',
    ],
  },

  // #1 — Strongest AI-native project for the current role
  {
    id: 1,
    title: 'Made In Heaven AI Dating App',
    // Two mobile screens side-by-side for the key high-risk moments (AI Dating App)
    videos: [
      '/case-studies/made-in-heaven/MIH/voice-ux.mp4',   // left
      '/case-studies/made-in-heaven/MIH/drip-cards.mp4', // right
    ],
    caseStudyUrl: '/case-studies/made-in-heaven/',
    liveUrl: 'https://app.madeinheaven.love', // live product
    hideExpand: true,
    tags: [
      'AI-Native Onboarding Flows',
      'Voice + Multimodal Input',
      'Designing for Model Uncertainty',
      'High-Stakes User Trust & Safety',
      'Gemini/Claude Integration',
      'AI Chatbot',
      'Supabase',
      'Typescript',
      '30+ Research Sessions',
      'React + Tailwind (Production)',
    ],
  },

  // AI Vision project
  {
    id: 2,
    title: 'Breguet AI Vision',
    video: null, // using image for now
    image: '/watchai-results.png',
    tryScannerUrl: 'https://watchai-3f503.web.app',
    detailsUrl: 'https://watchai-3f503.web.app/handoff',
    hideExpand: true,
    tags: [
      'Camera-Based Vision Model',
      'Real-time Catalog Matching',
      'Multimodal Product Identification',
      'Designing for AI Confidence Scores',
      'Handling Uncertain Outputs',
    ],
  },

  {
    id: 3,
    title: 'Lucid Web App',
    video: '/videos/lucid.mp4',
    link: 'https://lucidtracking.com',
    tags: [
      'Frontend and Backend in Javascript',
      'React',
      'Google Appscript and Ecosystem',
      'Api Intergration',
      'UX',
      'Data Viz',
      'Visual Design and Branding',
    ],
  },
  {
    id: 4,
    title: 'DC Comics Shop (Warner Bros)',
    video: '/videos/dcshop.mp4',
    link: 'https://shop.dc.com',
    className: 'portrait',
    tags: [
      'High-Traffic Production System',
      'Design Systems & Component Architecture',
      'Next.js + AWS Serverless',
      'Scalable Design-to-Code',
    ],
  },
  {
    id: 5,
    title: 'Functional Medicine App',
    video: '/videos/fmd.mp4',
    link: 'https://the-fmd.com',
    className: 'portrait',
    tags: [
      'Frontend and Backend in Javascript',
      'React',
      'Firebase',
      'Api Intergration',
      'UX',
      'Data Viz',
      'Visual Design and Branding',
    ],
  },
  {
    id: 6,
    title: 'Data Visualization Systems',
    video: '/videos/network.mp4',
    tags: [
      'Information Architecture',
      'Complex Data Interfaces',
      'React Prototyping',
    ],
  },
];

function App() {
  const [fullscreenId, setFullscreenId] = useState(null);

  return (
    <div className="App">
      <div className="header">
        <div>
          <h1>Enrique Florendo</h1>
          <p className="faded">
            Product Design Engineer: AI Native Interfaces and Internal Tools
          </p>
          <p className="faded">
            Antigravity/Cursor/Grok Build, Gemini/Claude/Grok, Vision/Voice/LLM Integration, Fullstack Design and Engineering, Web/App/TV
          </p>
        </div>
        <div className="contact">
          <a href="mailto:enriqueflorendo@gmail.com">
            <img src="/mail.svg" alt="Email" />
          </a>
          <a href="tel:6174073971">
            <img src="/call.svg" alt="Call" />
          </a>
          <a
            href="https://linkedin.com/in/enriqueflorendo"
            target="_blank"
            rel="noopener noreferrer"
            className="linkedin"
          >
            <i className="fa-brands fa-linkedin"></i>
          </a>
        </div>
      </div>

      {fullscreenId !== null && (
        <img
          src="/close.svg"
          className="close"
          alt="Close"
          onClick={() => setFullscreenId(null)}
        />
      )}

      {projects.map((item) => (
        <div
          key={item.id}
          className={`hero ${fullscreenId === item.id ? 'fullscreen' : ''}`}
        >
          <h2>
            {item.title}{' '}
            <span className="actions">
              {/* Only show expand icon if we're not in the big fallback state */}
              {!item.hideExpand && (!item.caseStudyUrl || item.video || item.videos) ? (
                <div>
                  <img
                    src="/expand.svg"
                    className="open"
                    alt="Expand"
                    title="Full Screen"
                    onClick={() => setFullscreenId(item.id)}
                  />
                </div>
              ) : null}

              {(item.link || item.liveUrl) && (
                <div>
                  <a
                    href={item.liveUrl || item.link}
                    target="_blank"
                    rel="noreferrer"
                    title="Visit live site"
                  >
                    <img
                      src="/open.svg"
                      className="open"
                      alt="Visit live site"
                    />
                  </a>
                </div>
              )}

              {/* Custom text buttons for Breguet AI Vision project */}
              {item.tryScannerUrl && (
                <a
                  href={item.tryScannerUrl}
                  className="action-btn"
                  target="_blank"
                  rel="noreferrer"
                >
                  Camera Scanner
                  <img src="/open.svg" alt="" />
                </a>
              )}
              {item.detailsUrl && (
                <a
                  href={item.detailsUrl}
                  className="action-btn"
                  target="_blank"
                  rel="noreferrer"
                >
                  Project Details
                  <img src="/open.svg" alt="" />
                </a>
              )}

              {item.caseStudyUrl && (item.video || item.videos) && (
                <a
                  href={item.caseStudyUrl}
                  className="case-study-btn"
                  title="View full case study"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View Case Study
                </a>
              )}
            </span>
          </h2>

          <div className="tags">
            {item.tags.map((tag, index) => (
              <span key={index} className="tag">
                {tag}
              </span>
            ))}
          </div>

          {item.videos ? (
            // Dual mobile screens (e.g. drip-cards + voice-ux for Made in Heaven)
            <div className="dual-video">
              {item.videos.map((videoSrc, index) => (
                <video
                  key={index}
                  src={videoSrc}
                  playsInline
                  loop
                  autoPlay
                  muted
                  preload="metadata"
                  onClick={() => setFullscreenId(null)}
                />
              ))}
            </div>
          ) : item.video ? (
            <video
              className={item.className || ''}
              src={item.video}
              poster={item.poster || undefined}
              playsInline
              loop
              autoPlay
              muted
              preload="metadata"
              onClick={() => setFullscreenId(null)}
              onError={() => {
                console.warn(`Video not found for "${item.title}": ${item.video}`);
              }}
            />
          ) : item.image ? (
            <img
              src={item.image}
              alt={item.title}
              className="hero-image"
              onClick={() => setFullscreenId(null)}
            />
          ) : item.caseStudyUrl ? (
            // Special fallback for Made In Heaven AI Dating App when case study assets aren't copied yet
            <div className="made-in-heaven-fallback">
              <h2 style={{ marginBottom: 8, color: '#fff' }}>Made In Heaven AI Dating App</h2>
              <p style={{ maxWidth: 420, marginBottom: 24, opacity: 0.8 }}>
                AI-native case study exploring voice input, high-risk onboarding, and designing for uncertain model outputs.
              </p>
              <a
                href={item.caseStudyUrl}
                className="big-button"
                target="_blank"
                rel="noopener noreferrer"
              >
                View Full Case Study →
              </a>
            </div>
          ) : (
            <div className="hero-placeholder" onClick={() => setFullscreenId(null)}>
              <span>Tap to view project</span>
            </div>
          )}
        </div>
      ))}

      {/* AI Tools & Process Section */}
      <div className="ai-process">
        <h3>How I Work with AI</h3>
        <div className="process-items">
          <div>
            <strong>Antigravity + Grok Build</strong> Primary tools for rapid design-to-production cycles. I use them to explore interaction patterns, generate and critique components, and move from concept to working code in hours instead of days.
          </div>
          <div>
            <strong>Designing for LLMs</strong> I think in terms of model behavior: streaming responses, confidence thresholds, multi-turn memory, and when to give users control vs. let the system act. This shows up in both Made In Heaven AI Dating App and Breguet AI Vision.
          </div>
          <div>
            <strong>Hands-on in Code</strong> I work full-stack across React, TypeScript, Tailwind, serverless architectures, and multimodal systems. I collaborate directly with AI engineers, contribute to pull requests, and care about both the quality of the user experience and the underlying implementation, including vision and voice interfaces.
          </div>
          <div>
            <strong>Prototyping Fast</strong> I regularly build high-fidelity prototypes and internal tools that get used in production. The goal is always clarity under uncertainty.
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
